import type { GuildIconKeys } from '../GuildIcon/GuildIcon'

export type QuickAction = {
  id: string
  label: string
  icon: GuildIconKeys
  keywords: string[]
  auth: 'always' | 'signed-in' | 'signed-out'
  type: 'navigate' | 'sign-out' | 'toggle-theme'
  /** Show in the default suggested actions (empty query). False = only appears when typed. */
  showByDefault: boolean
}

export const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'home',
    label: 'Home',
    icon: 'home',
    keywords: ['home', 'main', 'start', 'dashboard'],
    auth: 'always',
    type: 'navigate',
    showByDefault: false,
  },
  {
    id: 'create-event',
    label: 'Create an Event',
    icon: 'calendar',
    keywords: ['create event', 'new event'],
    auth: 'always',
    type: 'navigate',
    showByDefault: true,
  },
  {
    id: 'create-presentation',
    label: 'Create a Presentation',
    icon: 'projection-screen',
    keywords: ['create presentation', 'new presentation', 'create talk', 'new talk'],
    auth: 'always',
    type: 'navigate',
    showByDefault: true,
  },
  {
    id: 'create-guild',
    label: 'Create a Guild',
    icon: 'people-community',
    keywords: ['create guild', 'new guild', 'create community', 'new community'],
    auth: 'always',
    type: 'navigate',
    showByDefault: true,
  },
  {
    id: 'profile',
    label: 'Profile',
    icon: 'person',
    keywords: ['profile', 'my profile', 'account', 'me'],
    auth: 'signed-in',
    type: 'navigate',
    showByDefault: false,
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings',
    keywords: ['settings', 'preferences', 'account settings'],
    auth: 'signed-in',
    type: 'navigate',
    showByDefault: false,
  },
  {
    id: 'sign-out',
    label: 'Sign Out',
    icon: 'sign-out-filled',
    keywords: ['sign out', 'log out', 'logout', 'signout'],
    auth: 'signed-in',
    type: 'sign-out',
    showByDefault: false,
  },
  {
    id: 'toggle-theme',
    label: 'Toggle Dark Mode',
    icon: 'weather-moon',
    keywords: ['dark mode', 'light mode', 'theme', 'toggle theme', 'dark', 'light', 'night mode', 'appearance'],
    auth: 'always',
    type: 'toggle-theme',
    showByDefault: false,
  },
  {
    id: 'sign-in',
    label: 'Sign In',
    icon: 'person-arrow-right',
    keywords: ['sign in', 'log in', 'login', 'signin'],
    auth: 'signed-out',
    type: 'navigate',
    showByDefault: false,
  },
  {
    id: 'sign-up',
    label: 'Sign Up',
    icon: 'person-add',
    keywords: ['sign up', 'register', 'join', 'signup', 'create account'],
    auth: 'signed-out',
    type: 'navigate',
    showByDefault: false,
  },
]

export const filterQuickActions = (
  query: string,
  isSignedIn: boolean
): QuickAction[] => {
  const authFiltered = QUICK_ACTIONS.filter((action) => {
    if (action.auth === 'always') return true
    if (action.auth === 'signed-in') return isSignedIn
    if (action.auth === 'signed-out') return !isSignedIn
    return false
  })

  if (!query.trim()) return authFiltered.filter((action) => action.showByDefault)

  const q = query.toLowerCase().trim()

  return authFiltered.filter((action) => {
    if (action.label.toLowerCase().includes(q)) return true
    return action.keywords.some((kw) => kw.includes(q))
  })
}
